import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useShop, useTokens } from "../lib/hooks";
import { FullPageLoader, ShopMissing } from "../components/States";
import FloatingParticles from "../components/FloatingParticles";
import { Volume2, VolumeX, Zap, Users } from "lucide-react";
import {
  primeTTS,
  speakAnnouncement,
  testTTS,
  playDing,
} from "../lib/tts";

const AVG_SERVICE_MIN = 20;       // default per-customer service estimate
const NOW_SERVING_MIN = 15;       // assumed remaining for currently-serving customer

export default function LiveTVPage() {
  const { slug } = useParams();
  const { shop, loading: shopLoading } = useShop(slug);
  const { tokens, loading: tokensLoading } = useTokens(shop?.id);

  // Sound is ON by default on every fresh page load. The user can still mute
  // via the existing Volume button. Browsers may block actual audio playback
  // until a user gesture — we prime on mount AND on the very first interaction
  // so the next announcement plays without an extra click.
  const [audioOn, setAudioOn] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [testResult, setTestResult] = useState(null);
  // null | "ok-speech" | "ok-mp3" | "fail"
  const prevServingRef = useRef(null);
  // Stable anchor for the "Time Remaining" countdown — set once per serving
  // token so the countdown ticks smoothly even when DB timestamps are stale.
  const anchorRef = useRef({ id: null, anchorMs: 0 });

  // 1-second tick — drives ALL countdowns (now serving + each queued customer)
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Auto-prime audio: try immediately on mount (works on TV browsers / kiosk mode
  // that allow autoplay) and again on the first user gesture as a fallback for
  // strict desktop browsers. After the first prime succeeds, the listener detaches.
  useEffect(() => {
    primeTTS();
    let attached = true;
    const handler = () => {
      if (!attached) return;
      primeTTS();
      attached = false;
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
    window.addEventListener("pointerdown", handler);
    window.addEventListener("keydown", handler);
    return () => {
      attached = false;
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
  }, []);

  const { serving, waiting, waitingCount } = useMemo(() => {
    const serving = tokens.find((t) => t.status === "serving") || null;
    const waiting = tokens.filter((t) => t.status === "waiting");
    return { serving, waiting, waitingCount: waiting.length };
  }, [tokens]);

  // Ding + TTS — keyed on serving_started_at so Recall (which updates this
  // timestamp) re-announces the same token. We do NOT speak the customer
  // name on the TV — only "Token Number <n>, please proceed to Counter 1."
  useEffect(() => {
    if (!audioOn) return;
    const cur = serving?.token_number ?? null;
    const trigger = serving?.serving_started_at || null;
    if (cur && trigger && trigger !== prevServingRef.current) {
      playDing();
      const tokenSpoken = String(cur).split("").join(" ");
      setTimeout(
        () =>
          speakAnnouncement(
            `Token number ${tokenSpoken}, please proceed to Counter 1.`,
            { tokenNumber: String(cur) }
          ),
        700
      );
    }
    prevServingRef.current = trigger;
  }, [serving?.serving_started_at, serving?.token_number, audioOn]);

  async function testVoice() {
    // CRITICAL: prime BEFORE any async work so we stay inside the user gesture.
    primeTTS();
    setAudioOn(true);
    playDing();
    const res = await testTTS("1");
    if (res.ok) {
      setTestResult(res.channel === "speech" ? "ok-speech" : "ok-mp3");
    } else {
      setTestResult("fail");
    }
    setTimeout(() => setTestResult(null), 6000);
  }

  if (shopLoading || tokensLoading) return <FullPageLoader label="Loading TV display" />;
  if (!shop) return <ShopMissing slug={slug} />;

  const accent = shop.brand_color || "#E6C662";
  const clock = new Date(now);
  const hh = String(clock.getHours()).padStart(2, "0");
  const mm = String(clock.getMinutes()).padStart(2, "0");
  const ss = String(clock.getSeconds()).padStart(2, "0");

  // Remaining time for the currently-serving token. Strategy:
  //   1. Prefer `serving_started_at` (admin "Call Next" path sets this).
  //   2. Fall back to `created_at` if it falls inside the service window.
  //   3. As a last resort (legacy rows where both timestamps are stale), anchor
  //      to the moment we first observed this serving token on the TV — captured
  //      once in a ref so the countdown ticks instead of freezing at 00:00.
  // This guarantees the countdown is always visible AND moves every second.
  let servingRemainMs = null;
  let sMin = null;
  let sSec = null;
  if (serving) {
    const windowMs = NOW_SERVING_MIN * 60_000;
    let target = null;
    if (serving.serving_started_at) {
      const t = new Date(serving.serving_started_at).getTime() + windowMs;
      if (t > now) target = t;
    }
    if (target == null && serving.created_at) {
      const t = new Date(serving.created_at).getTime() + windowMs;
      if (t > now) target = t;
    }
    if (target == null) {
      // Stale data — pin an anchor for this serving token so subsequent ticks
      // produce a smooth countdown instead of being frozen at 00:00.
      if (anchorRef.current.id !== serving.id) {
        anchorRef.current = { id: serving.id, anchorMs: now };
      }
      target = anchorRef.current.anchorMs + windowMs;
    }
    servingRemainMs = Math.max(0, target - now);
    sMin = Math.floor(servingRemainMs / 60_000);
    sSec = Math.floor((servingRemainMs % 60_000) / 1000);
  }

  return (
    <div className="relative min-h-[100dvh] bg-void text-white overflow-hidden flex flex-col" data-testid="tv-page">
      <FloatingParticles color={accent} />

      {/* Soft brand orb */}
      <div
        className="pointer-events-none fixed -top-60 -left-60 rounded-full"
        style={{
          width: "60vmin", height: "60vmin",
          background: `radial-gradient(circle, ${accent}18 0%, transparent 60%)`,
          filter: "blur(40px)",
        }}
      />
      <div
        className="pointer-events-none fixed -bottom-60 -right-60 rounded-full"
        style={{
          width: "55vmin", height: "55vmin",
          background: `radial-gradient(circle, ${accent}14 0%, transparent 65%)`,
          filter: "blur(40px)",
        }}
      />

      {/* Top bar */}
      <header className="relative z-10 flex-shrink-0 px-[clamp(1rem,2.5vw,2.5rem)] pt-[clamp(0.75rem,1.5vw,1.5rem)] pb-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div
              className="font-numbers tracking-[0.35em] uppercase truncate"
              style={{ color: accent, fontSize: "clamp(0.55rem, 0.9vw, 0.85rem)" }}
            >
              ⚡ AIBox · Smart Queue · Live
            </div>
            <div
              className="font-heading text-white tracking-tight mt-1 truncate"
              style={{ fontSize: "clamp(1.25rem, 2.5vw, 2.75rem)", lineHeight: 1.05 }}
            >
              {shop.name}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div
              className="font-numbers tabular-nums leading-none"
              style={{ fontSize: "clamp(1.5rem, 3vw, 3.5rem)" }}
            >
              {hh}<span style={{ color: accent }} className="mx-0.5">:</span>{mm}
              <span
                className="ml-1 font-numbers opacity-60"
                style={{ fontSize: "clamp(0.65rem, 1vw, 1rem)", color: accent }}
              >
                {ss}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={testVoice}
                data-testid="tv-test-voice"
                className="rounded-full border border-gold/40 text-gold px-2.5 py-1 flex items-center gap-1.5 hover:bg-gold/10 transition-colors"
                style={{
                  fontSize: "clamp(0.55rem, 0.8vw, 0.7rem)",
                  fontFamily: '"IBM Plex Mono", monospace',
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
                title="Tap once to test voice + enable audio"
              >
                Test
              </button>
              <button
                onClick={() => setAudioOn((v) => !v)}
                data-testid="tv-audio-toggle"
                className="rounded-full border px-2.5 py-1 flex items-center gap-1.5"
                style={{
                  borderColor: audioOn ? `${accent}80` : "rgba(255,255,255,0.15)",
                  color: audioOn ? accent : "rgba(255,255,255,0.6)",
                  fontSize: "clamp(0.55rem, 0.8vw, 0.7rem)",
                  fontFamily: '"IBM Plex Mono", monospace',
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                {audioOn ? <Volume2 className="w-3 h-3" /> : <VolumeX className="w-3 h-3" />}
                {audioOn ? "Sound" : "Mute"}
              </button>
              <div
                className="rounded-full border px-2.5 py-1 flex items-center gap-1.5"
                style={{
                  borderColor: shop.is_open ? "rgba(34,197,94,0.4)" : "rgba(239,68,68,0.4)",
                  color: shop.is_open ? "#34d399" : "#f87171",
                  fontSize: "clamp(0.55rem, 0.8vw, 0.7rem)",
                  fontFamily: '"IBM Plex Mono", monospace',
                  letterSpacing: "0.25em",
                  textTransform: "uppercase",
                }}
              >
                <motion.span
                  className="inline-block w-1.5 h-1.5 rounded-full"
                  animate={shop.is_open ? { opacity: [1, 0.4, 1] } : {}}
                  transition={{ duration: 1.6, repeat: Infinity }}
                  style={{ background: shop.is_open ? "#34d399" : "#f87171" }}
                />
                {shop.is_open ? "Open" : "Closed"}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Sound-prompt overlay — appears when audio is muted so admin/staff
          knows to tap once (browsers block autoplay until user interaction) */}
      {!audioOn && (
        <button
          onClick={testVoice}
          data-testid="tv-enable-sound-overlay"
          className="fixed bottom-[clamp(2.5rem,5vw,4rem)] left-1/2 -translate-x-1/2 z-30 rounded-full border border-gold bg-void/90 backdrop-blur-md gold-glow px-5 py-2.5 flex items-center gap-2 hover:bg-gold/15 transition-colors animate-gold-pulse"
          style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "clamp(0.65rem, 0.95vw, 0.8rem)", letterSpacing: "0.25em", textTransform: "uppercase", color: "#E6C662" }}
        >
          <Volume2 className="w-3.5 h-3.5" /> Click to Enable Sound
        </button>
      )}

      {/* Voice test result banner — gives admin instant feedback */}
      {testResult && (
        <div
          className="fixed top-[clamp(4.5rem,8vw,7rem)] left-1/2 -translate-x-1/2 z-30 rounded-full backdrop-blur-md px-4 py-2 flex items-center gap-2 border"
          style={{
            background:
              testResult === "fail"
                ? "rgba(239,68,68,0.18)"
                : "rgba(16,185,129,0.18)",
            borderColor:
              testResult === "fail"
                ? "rgba(239,68,68,0.55)"
                : "rgba(16,185,129,0.55)",
            color: testResult === "fail" ? "#f87171" : "#34d399",
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: "clamp(0.65rem, 0.9vw, 0.8rem)",
            letterSpacing: "0.22em",
            textTransform: "uppercase",
          }}
          data-testid="tv-voice-test-result"
        >
          {testResult === "ok-speech" && "Voice Ready · Browser TTS"}
          {testResult === "ok-mp3" && "Voice Ready · MP3 Fallback (TV mode)"}
          {testResult === "fail" && "Voice Test Failed"}
        </div>
      )}

      {/* MAIN GRID — responsive: side-by-side on landscape/desktop, stacked on portrait/mobile */}
      <main
        className="relative z-10 flex-1 grid gap-[clamp(0.75rem,1.5vw,1.5rem)] px-[clamp(1rem,2.5vw,2.5rem)] pb-2 min-h-0"
        style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
      >
        <div
          className="grid gap-[clamp(0.75rem,1.5vw,1.5rem)] min-h-0"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 22rem), 1fr))" }}
        >
          {/* NOW SERVING */}
          <NowServingCard
            serving={serving}
            accent={accent}
            sMin={sMin}
            sSec={sSec}
          />

          {/* NEXT IN LINE */}
          <NextInLine
            waiting={waiting}
            accent={accent}
            waitingCount={waitingCount}
            servingRemainMs={servingRemainMs}
          />
        </div>
      </main>

      {/* Bottom ticker */}
      <footer className="relative z-10 flex-shrink-0 border-t overflow-hidden py-[clamp(0.5rem,1vw,0.875rem)]"
        style={{ borderColor: `${accent}22`, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(20px)" }}>
        <div className="flex whitespace-nowrap animate-slow-marquee tracking-[0.35em] uppercase opacity-80"
          style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "clamp(0.65rem, 0.9vw, 0.95rem)", color: accent }}>
          {["a", "b"].map((k) => (
            <span key={k} className="px-10">
              Welcome to {shop.name} · Thank you for choosing us · {shop.tagline || "Premium Experience"} · Powered by AIBox Global · AIBoxHub.in ·
            </span>
          ))}
        </div>
      </footer>
    </div>
  );
}

function NowServingCard({ serving, accent, sMin, sSec }) {
  return (
    <section
      className="relative rounded-[clamp(1rem,1.5vw,1.5rem)] border overflow-hidden p-[clamp(1rem,2vw,2rem)] flex flex-col min-h-0"
      style={{
        borderColor: `${accent}22`,
        background: "linear-gradient(180deg, rgba(15,15,15,0.85) 0%, rgba(8,8,8,0.85) 100%)",
        backdropFilter: "blur(20px)",
      }}
      data-testid="tv-now-serving"
    >
      <div className="flex items-center gap-2"
        style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "clamp(0.6rem, 1vw, 0.85rem)", letterSpacing: "0.4em", textTransform: "uppercase", color: accent }}>
        <Zap className="w-3.5 h-3.5" /> Now Serving
      </div>

      <div className="flex-1 flex flex-col items-center justify-center mt-3 min-h-0">
        <AnimatePresence mode="popLayout">
          <motion.div
            key={serving?.id || "none"}
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.96 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex flex-col items-center"
          >
            {serving ? (
              <>
                <ResponsiveAvatar
                  name={serving.customer_name}
                  tokenNumber={serving.token_number}
                  brandColor={accent}
                />
                <div
                  className="mt-3 font-numbers tracking-[0.3em] uppercase text-center"
                  style={{ color: "rgba(255,255,255,0.55)", fontSize: "clamp(0.65rem, 1vw, 0.9rem)" }}
                >
                  {serving.service_name || "—"}
                </div>

                {/* Time remaining countdown — always rendered when there is
                    a serving token. sMin/sSec are guaranteed non-null by the
                    parent component when `serving` is truthy. */}
                <div
                  className="mt-[clamp(0.75rem,1.5vw,1.25rem)] rounded-2xl border px-[clamp(1.5rem,2.5vw,3rem)] py-[clamp(0.5rem,1vw,1rem)] text-center"
                  style={{ borderColor: `${accent}33`, background: "rgba(0,0,0,0.5)" }}
                  data-testid="tv-time-remaining"
                >
                  <div
                    className="font-numbers tracking-[0.3em] uppercase"
                    style={{ color: "rgba(255,255,255,0.4)", fontSize: "clamp(0.55rem, 0.9vw, 0.75rem)" }}
                  >
                    Time Remaining
                  </div>
                  <div
                    className="font-numbers tabular-nums leading-none mt-1"
                    style={{
                      color: sMin != null && sMin < 2 ? "#34d399" : accent,
                      fontSize: "clamp(1.75rem, 4vw, 3.25rem)",
                      textShadow: `0 0 30px ${sMin != null && sMin < 2 ? "#34d39955" : accent + "77"}`,
                    }}
                  >
                    {String(sMin ?? 0).padStart(2, "0")}:{String(sSec ?? 0).padStart(2, "0")}
                  </div>
                </div>
              </>
            ) : (
              <div className="font-heading text-center"
                style={{ fontSize: "clamp(1.5rem, 3vw, 3rem)", color: "rgba(255,255,255,0.3)" }}>
                Awaiting next call…
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}

// Avatar that scales gracefully to its container without measuring DOM
function ResponsiveAvatar({ name, tokenNumber, brandColor }) {
  return (
    <div className="flex flex-col items-center">
      <div
        className="relative"
        style={{ width: "clamp(180px, 26vmin, 360px)", aspectRatio: "1 / 1" }}
      >
        <RotatingRing brandColor={brandColor} />
        <PulsingHalo brandColor={brandColor} />
        <InnerDisk name={name} tokenNumber={tokenNumber} brandColor={brandColor} />
      </div>
      {name && (
        <motion.div
          key={name}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="mt-[clamp(0.5rem,1.2vw,1.25rem)] font-heading text-white text-center tracking-tight"
          style={{ fontSize: "clamp(1.5rem, 3.5vw, 3.5rem)", lineHeight: 1.05 }}
        >
          {name}
        </motion.div>
      )}
    </div>
  );
}

// CircularAvatarFluid wrapped with width:100% to fill its slot. (legacy - kept as stub)
// CircularAvatarFluid removed; ResponsiveAvatar handles all sizing now.

function RotatingRing({ brandColor }) {
  return (
    <motion.div
      className="absolute inset-0 rounded-full"
      animate={{ rotate: 360 }}
      transition={{ duration: 14, repeat: Infinity, ease: "linear" }}
      style={{
        background: `conic-gradient(from 0deg, transparent 0deg, ${brandColor}cc 60deg, transparent 140deg, ${brandColor}88 220deg, transparent 320deg)`,
        filter: "blur(10px)",
        opacity: 0.85,
      }}
    />
  );
}

function PulsingHalo({ brandColor }) {
  return (
    <motion.div
      className="absolute inset-4 rounded-full"
      animate={{ scale: [1, 1.05, 1], opacity: [0.5, 0.85, 0.5] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
      style={{ boxShadow: `0 0 100px 8px ${brandColor}55, inset 0 0 50px ${brandColor}22` }}
    />
  );
}

function InnerDisk({ name, tokenNumber, brandColor }) {
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "?";
  return (
    <div className="absolute inset-[6%] rounded-full overflow-hidden flex flex-col items-center justify-center"
      style={{
        background: `radial-gradient(circle at 30% 25%, #1a1a1a 0%, #0a0a0a 60%, #050505 100%)`,
        boxShadow: `inset 0 0 60px ${brandColor}1f, inset 0 4px 30px rgba(255,255,255,0.04), 0 0 60px ${brandColor}33`,
        border: `1px solid ${brandColor}55`,
      }}>
      <div className="absolute inset-0 rounded-full"
        style={{ background: `radial-gradient(ellipse at 30% 20%, ${brandColor}33 0%, transparent 45%)` }} />

      {/* Token number — the MAIN element */}
      <motion.div
        key={tokenNumber}
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 font-numbers font-semibold tabular-nums"
        style={{
          fontSize: "clamp(4rem, 13vmin, 11rem)",
          lineHeight: 1,
          color: brandColor,
          textShadow: `0 0 40px ${brandColor}cc, 0 2px 0 rgba(0,0,0,0.4)`,
          letterSpacing: "-0.03em",
        }}
      >
        {tokenNumber ?? "—"}
      </motion.div>

      {/* Small initials badge under the token */}
      {name && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="relative z-10 mt-1 font-heading italic"
          style={{
            fontSize: "clamp(0.95rem, 2.2vmin, 1.6rem)",
            color: "rgba(255,255,255,0.55)",
            letterSpacing: "0.05em",
          }}
        >
          {initials}
        </motion.div>
      )}
    </div>
  );
}

function NextInLine({ waiting, accent, waitingCount, servingRemainMs }) {
  // Fixed visual capacity (rows we *aim* to render at one time). The list is
  // not truncated — when there are more, the inner column auto-scrolls.
  const ROWS_VISIBLE = 5;
  const total = waiting.length;
  // Cap rendered rows to keep DOM lean on very long queues.
  const CAP = 30;
  const visibleList = waiting.slice(0, CAP);
  // Auto-scroll only when there are more rows than fit in the viewport.
  const shouldScroll = total > ROWS_VISIBLE;

  // Duration scales with list size — slow, continuous and elegant.
  const scrollDurationSec = Math.max(20, visibleList.length * 4);

  return (
    <section
      className="relative rounded-[clamp(1rem,1.5vw,1.5rem)] border p-[clamp(1rem,2vw,2rem)] flex flex-col min-h-0"
      style={{
        borderColor: `${accent}22`,
        background: "linear-gradient(180deg, rgba(15,15,15,0.85) 0%, rgba(8,8,8,0.85) 100%)",
        backdropFilter: "blur(20px)",
      }}
      data-testid="tv-next-list"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"
          style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: "clamp(0.6rem, 1vw, 0.85rem)", letterSpacing: "0.4em", textTransform: "uppercase", color: accent }}>
          <Users className="w-3.5 h-3.5" /> Next In Line
        </div>
        <div className="rounded-full border px-2.5 py-1 flex items-center gap-1.5"
          style={{ borderColor: `${accent}55`, color: accent, fontFamily: '"IBM Plex Mono", monospace', fontSize: "clamp(0.55rem, 0.8vw, 0.75rem)", letterSpacing: "0.25em", textTransform: "uppercase" }}>
          <span className="font-semibold tabular-nums" style={{ color: accent }}>{waitingCount}</span> Waiting
        </div>
      </div>

      <div className="mt-[clamp(0.75rem,1.5vw,1.25rem)] flex-1 min-h-0 overflow-hidden relative">
        {visibleList.length === 0 ? (
          <div className="h-full flex items-center justify-center font-heading text-center"
            style={{ color: "rgba(255,255,255,0.3)", fontSize: "clamp(1rem, 2vw, 1.75rem)" }}>
            The queue is empty.
          </div>
        ) : (
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              // Soft fade at top & bottom so scrolling rows fade in/out gracefully.
              maskImage:
                "linear-gradient(to bottom, transparent 0, #000 6%, #000 94%, transparent 100%)",
              WebkitMaskImage:
                "linear-gradient(to bottom, transparent 0, #000 6%, #000 94%, transparent 100%)",
            }}
          >
            <div
              className={shouldScroll ? "animate-vscroll" : ""}
              style={{
                // Slow, continuous, elegant — one full cycle in scrollDurationSec.
                animationDuration: `${scrollDurationSec}s`,
              }}
            >
              {/* When scrolling, render the rows TWICE for a seamless loop. When
                  the queue fits in view, render once — no duplicate. */}
              {(shouldScroll ? [0, 1] : [0]).map((copy) => (
                <div
                  key={copy}
                  className="grid gap-[clamp(0.4rem,0.8vw,0.7rem)] pb-[clamp(0.4rem,0.8vw,0.7rem)]"
                  style={{ gridTemplateColumns: "minmax(0, 1fr)" }}
                >
                  {visibleList.map((t, idx) => (
                    <QueueRow
                      key={`${copy}-${t.id}`}
                      token={t}
                      idx={idx}
                      accent={accent}
                      servingRemainMs={servingRemainMs}
                      duplicate={copy === 1}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function QueueRow({ token, idx, accent, servingRemainMs, duplicate }) {
  // Per-row ETA in ms = remaining time of the serving customer + AVG_SERVICE_MIN
  // for each customer ahead in the queue. Updates every second because the
  // parent re-renders on each `now` tick.
  const remainSec = Math.max(0, Math.floor((servingRemainMs ?? 0) / 1000) + idx * AVG_SERVICE_MIN * 60);
  const m = Math.floor(remainSec / 60);
  const s = remainSec % 60;
  const highlight = !duplicate && idx === 0;
  return (
    <div
      className="rounded-xl border flex items-center gap-[clamp(0.5rem,1vw,1rem)] px-[clamp(0.75rem,1.5vw,1.25rem)] py-[clamp(0.5rem,1vw,0.875rem)]"
      style={{
        borderColor: highlight ? `${accent}55` : "rgba(255,255,255,0.06)",
        background: highlight
          ? `linear-gradient(135deg, ${accent}10, ${accent}05, transparent)`
          : "rgba(0,0,0,0.35)",
      }}
      aria-hidden={duplicate ? "true" : undefined}
    >
      <div
        className="font-numbers tabular-nums flex-shrink-0"
        style={{ color: accent, fontSize: "clamp(0.95rem, 1.5vw, 1.4rem)", fontWeight: 600, minWidth: "clamp(2.5rem, 4vw, 4rem)" }}
      >
        #{token.token_number}
      </div>
      <MiniAvatarFluid name={token.customer_name} brandColor={accent} />
      <div className="flex-1 min-w-0">
        <div className="font-heading text-white truncate"
          style={{ fontSize: "clamp(0.95rem, 1.5vw, 1.5rem)", lineHeight: 1.1 }}>
          {token.customer_name}
        </div>
        <div className="font-numbers tracking-[0.25em] uppercase truncate"
          style={{ color: "rgba(255,255,255,0.45)", fontSize: "clamp(0.55rem, 0.8vw, 0.75rem)", marginTop: "2px" }}>
          {token.service_name || "—"}
        </div>
      </div>
      <div
        className="flex-shrink-0 rounded-md border px-2 py-1 text-right"
        style={{ borderColor: `${accent}55`, background: "rgba(0,0,0,0.5)" }}
      >
        <div className="font-numbers tracking-[0.2em] uppercase"
          style={{ color: "rgba(255,255,255,0.4)", fontSize: "clamp(0.5rem, 0.7vw, 0.65rem)" }}>
          Wait
        </div>
        <div className="font-numbers tabular-nums leading-none"
          style={{ color: accent, fontSize: "clamp(0.85rem, 1.2vw, 1.1rem)" }}>
          {m}m {String(s).padStart(2, "0")}s
        </div>
      </div>
    </div>
  );
}

function MiniAvatarFluid({ name, brandColor }) {
  const initials = (name || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase() || "?";
  return (
    <div
      className="relative flex-shrink-0 rounded-full overflow-hidden flex items-center justify-center"
      style={{
        width: "clamp(34px, 4vw, 60px)",
        height: "clamp(34px, 4vw, 60px)",
        border: `1px solid ${brandColor}55`,
        background: `radial-gradient(circle at 30% 25%, #1a1a1a 0%, #050505 80%)`,
        boxShadow: `0 0 20px ${brandColor}33`,
      }}
    >
      <span
        className="font-heading italic"
        style={{
          color: brandColor,
          fontSize: "clamp(0.95rem, 1.8vw, 1.6rem)",
          lineHeight: 1,
          letterSpacing: "-0.03em",
          textShadow: `0 0 12px ${brandColor}aa`,
        }}
      >
        {initials}
      </span>
    </div>
  );
}
